import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";

import type { AccessDenialReason } from "@/lib/access";
import { cn } from "@/lib/utils";
import { NvButton } from "./nv-button";
import { NvCloseIcon } from "./nv-icons";
import { NvLucideXIcon } from "./nv-icons-finder";

// Front popups (Figma "Dialog / …", ver.6): dim #1A1C21 45%, white card r12, padding 28, gap 16, title 500 18/22 + brand Close icon.

interface DialogBaseProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  className?: string;
}

function DialogShell({
  open,
  onOpenChange,
  className,
  children,
  ...contentProps
}: DialogBaseProps & { children: React.ReactNode } & React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-[rgb(26_28_33_/_0.45)]" />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-1/2 top-1/2 z-50 flex max-h-[calc(100vh-48px)] w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2 flex-col gap-4 rounded-nv-card bg-nv-surface p-7 font-sans focus:outline-none",
            className,
          )}
          {...contentProps}
        >
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export interface NvAlertDialogProps extends DialogBaseProps {
  message: React.ReactNode;
  // Buttons, right-aligned (e.g. NvButton "확인")
  actions: React.ReactNode;
}

// Message popup (Figma 203:6751 "등록 공급사 이메일 가입 불가", 440 wide)
function NvAlertDialog({ message, actions, className, ...base }: NvAlertDialogProps) {
  return (
    <DialogShell {...base} role="alertdialog" aria-describedby={undefined} className={cn("max-w-[440px]", className)}>
      <DialogPrimitive.Title className="whitespace-pre-line text-nv-body-lg font-normal text-nv-text-primary">
        {message}
      </DialogPrimitive.Title>
      <div className="flex items-center justify-end gap-2">{actions}</div>
    </DialogShell>
  );
}

export interface NvModalProps extends DialogBaseProps {
  title: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  // Figma widths: 760 (약관 전문 보기)
  width?: number;
  // 닫기 아이콘 바꿔 쓰기 (ver.7 약관 전문 보기: Lucide x 24px). 없으면 기존 브랜드 닫기 아이콘
  closeIcon?: React.ReactNode;
}

// Titled popup (Figma 203:6764 "약관 전문 보기"): title 18/22 medium + Close icon
function NvModal({ title, children, footer, width = 760, closeIcon, className, ...base }: NvModalProps) {
  return (
    <DialogShell {...base} aria-describedby={undefined} style={{ maxWidth: width }} className={className}>
      <div className="flex items-center justify-between">
        <DialogPrimitive.Title className="text-[18px] font-medium leading-[22px] text-nv-text-primary">{title}</DialogPrimitive.Title>
        <DialogPrimitive.Close
          aria-label="닫기"
          className="rounded-nv-pill text-nv-text-primary hover:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-nv-brand"
        >
          {closeIcon ?? <NvCloseIcon size={18} />}
        </DialogPrimitive.Close>
      </div>
      {children}
      {footer && <div className="flex items-center justify-center gap-2">{footer}</div>}
    </DialogShell>
  );
}

export interface NvConsentPromptDialogProps extends DialogBaseProps {
  title: React.ReactNode;
  message: React.ReactNode;
  // 동의서 이름 줄 (예: "[선택] 개인정보 제3자 제공 동의서") + 오른쪽 [보기 ›]
  itemLabel: React.ReactNode;
  onView: () => void;
  onAgree: () => void;
  agreeing?: boolean;
  // 왼쪽(거절) · 오른쪽(동의) 버튼 이름과 거절 동작. 기본은 [닫기](팝업 닫기) · [동의하기]
  declineLabel?: React.ReactNode;
  agreeLabel?: React.ReactNode;
  onDecline?: () => void;
}

// 동의 안내 (Figma ver.7 "개인정보 제3자 제공 동의 안내 (팝업)" 847:57620 · "선택 동의 미동의 안내 (팝업)" 847:57555): 440 wide,
// 제목 18/700, 회색 동의서 줄(h45 · 반경 8 · #F3F4F6) + [보기 ›](13/500 주황), 버튼 높이 46으로 맞춤
function NvConsentPromptDialog({
  title,
  message,
  itemLabel,
  onView,
  onAgree,
  agreeing,
  declineLabel = "닫기",
  agreeLabel = "동의하기",
  onDecline,
  className,
  ...base
}: NvConsentPromptDialogProps) {
  return (
    <DialogShell {...base} className={cn("max-w-[440px]", className)}>
      <DialogPrimitive.Title className="text-[18px] font-bold leading-[22px] text-nv-text-primary">{title}</DialogPrimitive.Title>
      <DialogPrimitive.Description className="whitespace-pre-line text-[14px] leading-[22px] text-nv-text-secondary">{message}</DialogPrimitive.Description>
      <div className="flex h-[45px] items-center justify-between gap-3 rounded-nv-md bg-nv-bg-subtle px-4">
        <span className="text-[14px] leading-[17px] text-nv-text-primary">{itemLabel}</span>
        <button type="button" onClick={onView} className="shrink-0 text-[13px] font-medium leading-4 text-nv-brand hover:underline">
          보기 ›
        </button>
      </div>
      <div className="flex items-stretch justify-end gap-2">
        <NvButton variant="secondary" onClick={onDecline ?? (() => base.onOpenChange(false))}>
          {declineLabel}
        </NvButton>
        <NvButton onClick={onAgree} disabled={agreeing}>
          {agreeLabel}
        </NvButton>
      </div>
    </DialogShell>
  );
}

export interface NvAccessDialogProps extends DialogBaseProps {
  reason: AccessDenialReason | null;
  // Body for 비회원 / 공급자 — the Figma copy names HOME features, so each screen passes its own line.
  loginMessage?: React.ReactNode;
  forbiddenMessage?: React.ReactNode;
  onLogin?: () => void;
  onSignup?: () => void;
}

const ACCESS_COPY: Record<AccessDenialReason, { title: string; body: React.ReactNode }> = {
  LOGIN_REQUIRED: { title: "로그인이 필요한 서비스입니다", body: "에이전트 상세, Agent Finder, AI Assistant는\n로그인 후 이용할 수 있습니다." },
  PENDING_APPROVAL: { title: "가입 검토가 진행 중입니다", body: "승인이 완료되면 이용할 수 있습니다.\n승인 결과는 가입하신 메일로 안내드립니다." },
  // 공급자 문구는 설계서에 "기획 확정 필요" — 미팅 전 임시 문구 (가정 A34)
  USER_TYPE_FORBIDDEN: { title: "이용 권한 안내", body: "수요자 · PwC 내부 회원만 이용할 수 있습니다." },
};

// 이용 권한 안내 (Figma 203:13825): 440 wide, title 18/22 medium + Close icon, body 14/22.4, buttons right-aligned.
// 비회원 [회원가입][로그인] · 승인 대기 · 공급자 [확인].
function NvAccessDialog({ reason, loginMessage, forbiddenMessage, onLogin, onSignup, className, ...base }: NvAccessDialogProps) {
  const copy = ACCESS_COPY[reason ?? "LOGIN_REQUIRED"];
  const body = reason === "LOGIN_REQUIRED" ? (loginMessage ?? copy.body) : reason === "USER_TYPE_FORBIDDEN" ? (forbiddenMessage ?? copy.body) : copy.body;
  const close = () => base.onOpenChange(false);
  return (
    <DialogShell {...base} open={base.open && reason !== null} className={cn("max-w-[440px]", className)}>
      <div className="flex items-center justify-between">
        <DialogPrimitive.Title className="text-[18px] font-medium leading-[22px] text-nv-text-primary">{copy.title}</DialogPrimitive.Title>
        {/* ver.7 닫기 아이콘 = Lucide x 24px (10/13 제안 06) — 줄 높이가 24가 돼 팝업 높이가 202예요 */}
        <DialogPrimitive.Close
          aria-label="닫기"
          className="rounded-nv-pill text-nv-text-primary hover:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-nv-brand"
        >
          <NvLucideXIcon size={24} />
        </DialogPrimitive.Close>
      </div>
      <DialogPrimitive.Description className="whitespace-pre-line text-[14px] leading-[22px] text-nv-text-secondary">{body}</DialogPrimitive.Description>
      <div className="flex items-center justify-end gap-2">
        {reason === "LOGIN_REQUIRED" ? (
          <>
            <NvButton variant="secondary" onClick={onSignup}>
              회원가입
            </NvButton>
            <NvButton className="h-[46px]" onClick={onLogin}>
              로그인
            </NvButton>
          </>
        ) : (
          <NvButton onClick={close}>확인</NvButton>
        )}
      </div>
    </DialogShell>
  );
}

export { NvAlertDialog, NvModal, NvAccessDialog, NvConsentPromptDialog };
